import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Only the home page has the dark hero the header can float over.
  const overHero = location.pathname === '/';
  const transparent = overHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navigationItems = [
    { name: 'Main Page', path: '/' },
    { name: 'Profile', path: '/profile' },
    { name: 'HSE-Q', path: '/hse-q' },
    { name: 'Fleet', path: '/fleet' },
    { name: 'Projects', path: '/projects' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const isActiveRoute = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? 'bg-transparent py-2'
          : 'bg-white/95 backdrop-blur-md shadow-lg'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-4">
            <img
              src="/logo.jpg"
              alt="Petromarine Logo"
              className="h-12 w-auto rounded-lg"
            />
            <div>
              <h1
                className={`font-marine text-xl font-semibold tracking-widest uppercase transition-colors duration-500 ${
                  transparent ? 'text-white' : 'text-gray-900'
                }`}
              >
                PetroMarine
              </h1>
              <p
                className={`text-xs tracking-[0.2em] uppercase transition-colors duration-500 ${
                  transparent ? 'text-blue-100/80' : 'text-gray-600'
                }`}
              >
                Offshore Projects Support Co.
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`nav-link-line ${transparent ? 'on-dark' : ''} ${
                  isActiveRoute(item.path) ? 'active' : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2 transition-colors duration-500 ${
              transparent ? 'text-white' : 'text-gray-600 hover:text-ocean-600'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav
            className={`lg:hidden py-4 border-t ${
              transparent
                ? 'border-white/20 bg-[#06254c]/90 backdrop-blur-md rounded-b-2xl'
                : 'border-gray-200'
            }`}
          >
            <div className="flex flex-col space-y-2 px-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`nav-link-line ${transparent ? 'on-dark' : ''} ${
                    isActiveRoute(item.path) ? 'active' : ''
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
